/**
 * Types for the /api/forecast response and the underlying spots.json shape.
 * Generated against api/forecast.js — keep in sync.
 */

export const COMPASS_DIRS = [
  "N", "NNE", "NE", "ENE",
  "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW",
  "W", "WNW", "NW", "NNW",
] as const;

export type CompassDir = (typeof COMPASS_DIRS)[number];

export type MatchType = "good" | "ok" | "bad";
export type Level = "prime" | "good" | "marginal" | "unlikely" | "skip";

export interface Coords {
  lat: number;
  lon: number;
}

export interface WindRange {
  dirs: CompassDir[];
  speed_min: number;
  speed_max: number;
}

/**
 * Top-level geographic region. Compass-based buckets that absorb both
 * coast and inland-lake spots. See [docs/regions.md](../../docs/regions.md)
 * for the full taxonomy and rationale.
 */
export type Region =
  | "stockholm"
  | "east-sweden"
  | "south-sweden"
  | "west-sweden"
  | "central-sweden"
  | "gotland"
  | "oland"
  | "north-sweden";

/**
 * Sweden's 21 official län. Acts as the subregion layer between Region
 * and Area — surfaced as a subline under each Region in the picker.
 */
/** Runtime list — kept in sync with the Lan union below. Needed because
 *  TS types disappear at runtime and the dev/spots reverse-geocoder has
 *  to match Nominatim's "<X> län" strings against the known values. */
export const LAN_VALUES = [
  "Stockholm",
  "Uppsala",
  "Södermanland",
  "Östergötland",
  "Jönköping",
  "Kronoberg",
  "Kalmar",
  "Skåne",
  "Blekinge",
  "Halland",
  "Västra Götaland",
  "Värmland",
  "Örebro",
  "Västmanland",
  "Dalarna",
  "Gävleborg",
  "Västernorrland",
  "Jämtland",
  "Västerbotten",
  "Norrbotten",
  "Gotland",
] as const;
export type Lan = (typeof LAN_VALUES)[number];

export interface Spot {
  id: string;
  name: string;
  /** Top-level navigation grouping. */
  region: Region;
  /**
   * Official län this spot sits in. Optional — Öland is its own region
   * but administratively part of Kalmar län; we leave subregion empty
   * there so Kalmar doesn't appear under both regions.
   */
  subregion?: Lan;
  /** Granular sub-area used as a list-header inside a region. */
  area: string;
  /**
   * Named body of water the spot launches into — e.g. "Himmerfjärden",
   * "Mälaren", "Östersjön", "Baggensfjärden". Optional; some spots
   * (open coast, unnamed lake) don't have a useful named water body.
   * Distinct from `area` (a land-side locality) so the UI can show
   * both: "Boo — Baggensfjärden".
   */
  waterBody?: string;
  coords: Coords;
  ideal: WindRange;
  secondary: WindRange;
  /** Free-text description in the Big Wind voice. */
  note?: string;
  /** Free-text water character (chop, fetch, depth, etc.). */
  water?: string;
  /** Free-text access notes (parking, gate, walk to launch). */
  access?: string;
  priority: number;

  // ── Structured tags ─────────────────────────────────────────────────────
  /**
   * Hazards extracted into discrete tags. Examples: "Stones", "Reefs",
   * "Currents", "Swimmers", "Ships", "Bridge", "Mole", "Wave breaker".
   */
  hazards?: string[];
  /**
   * What's on site, beyond just parking. Examples: "Camping", "Toilet",
   * "Shower", "Food", "Shop", "Rental", "School", "Repair", "Rescue",
   * "Rooms".
   */
  facilities?: string[];
  /**
   * Water character tags. Examples: "Flat", "Chop", "Shallow",
   * "Small wave", "Medium wave", "Big wave".
   */
  waterTypes?: string[];
  /**
   * Best-months tags from the kiteforum data. Examples: "April",
   * "May", "September", "October". Used for future seasonal filtering.
   */
  bestMonths?: string[];
  /**
   * Free-form curator tags — anything useful that doesn't fit the
   * structured buckets above. Examples: "Beginner friendly", "Wing
   * launch", "Crowded weekends", "Algae summers", "Foiling ok",
   * "Lighthouse view". Surfaced in the dev/spots search blob and
   * intended for future user-facing chips.
   */
  tags?: string[];

  // ── Curation status ─────────────────────────────────────────────────────
  /**
   * True when `ideal`/`secondary` came from the scraper's default
   * (8-26 m/s) instead of per-spot curation. UI can show a hint or
   * a filter chip. Speed-match scoring still works but doesn't
   * differentiate these spots from each other on speed.
   */
  defaultWindRange?: boolean;
}

export interface WindHour {
  speedMs: number;
  dirDeg: number;
  gustMs: number;
  /** Air temperature, °C. Populated by SMHI's `air_temperature` for hours
   *  it covers, by Open-Meteo's `temperature_2m` for the rest. */
  tempC: number | null;
  /**
   * Water temperature, °C. Reserved field — not populated by either current
   * provider; a future SMHI marine-observations pass will look up the
   * nearest coastal/lake station per spot and fill this in. Optional so the
   * API can omit it cleanly until then.
   */
  waterTempC?: number | null;
  time: string;
  isNow: boolean;
}

export interface ScoredHour extends WindHour {
  score: number;
  level: Level;
}

export interface Band {
  score: number;
  level: Level;
  meanSpeedMs: number;
}

/** A scored day is either a real forecast (`level !== "skip"`) or a skip placeholder. */
export type ScoredDay = ScoredDayFull | ScoredDaySkip;

export interface ScoredDayFull {
  date: string;
  dayOffset: number;
  speedMinMs: number;
  speedMaxMs: number;
  gustMaxMs: number;
  dirDeg: number;
  dirSet: CompassDir[];
  score: number;
  matchType: MatchType;
  level: Exclude<Level, "skip">;
  bands: Band[];
  hours: ScoredHour[];
}

export interface ScoredDaySkip {
  date: string;
  dayOffset: number;
  level: "skip";
  score: 0;
  bands: [];
  hours: [];
}

export interface EnrichedSpot extends Spot {
  score: number;
  matchType: MatchType;
  reason?: string;
  wind: WindHour | null;
  windHours: WindHour[];
  days: ScoredDay[];
}

export interface ReferenceWind {
  dir: CompassDir;
  speedMs: number;
  gustMs: number;
  tempC: number | null;
  time: string;
}

export interface ForecastResponse {
  fetchedAt: string;
  referenceWind: ReferenceWind;
  spots: EnrichedSpot[];
}

/** Speed unit used for display. m/s is the canonical wire format. */
export type SpeedUnit = "kn" | "ms" | "kmh" | "mph";
